import { useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import CustomTextField from "../../CustomTextField";
import {
  AccountData,
  NotificationType,
  TeamMember,
  TeamMemberRole,
} from "@markit/common.types";
import RectangleButton from "../../Buttons/RectangleButton";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import CustomCheckbox from "../../CustomCheckbox";
import { API } from "../../../API";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import isEqual from "lodash.isequal";
import { fetchUserDataFromEmail } from "../../../utils/userUtils";
import AlertContainer from "../../Containers/AlertContainer";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { fetchSingleUser } from "../../../utils/FetchSingleData";

type CreateTeamMemberModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  existingTeamMember?: TeamMember;
};

const CreateTeamMemberModal = (props: CreateTeamMemberModalProps) => {
  const { isVisible, setIsVisible, existingTeamMember } = props;
  const dispatch = useDispatch();
  const { accountData, followingAccountData } =
    useSelector(getAccountState).account;
  const [teamMember, setTeamMember] = useState<TeamMember>({
    email: "",
    fullName: "",
    uid: "",
    createdAt: "",
    password: "",
    role: TeamMemberRole.FULL_ACCESS,
  });
  const [adminRole, setAdminRole] = useState<TeamMemberRole | undefined>(
    undefined
  );
  const [memberNameError, setMemberNameError] = useState("");
  const [memberEmailError, setMemberEmailError] = useState("");
  const [alertText, setAlertText] = useState("");
  const [savingMember, setSavingMember] = useState(false);
  const [memberUserData, setMemberUserData] = useState<AccountData>();

  const styles = {
    errorText: { fontSize: 12, color: Colors.RED2 },
  };

  useAsyncOnMount(async () => {
    if (existingTeamMember) {
      setTeamMember(existingTeamMember);
      setAdminRole(existingTeamMember.role);
      const userData = await fetchSingleUser(
        existingTeamMember.uid,
        followingAccountData
      );
      if (userData) {
        setMemberUserData(userData);
      }
    }
  });

  const madeChanges = useMemo(
    () =>
      existingTeamMember &&
      (!isEqual(existingTeamMember, teamMember) ||
        !isEqual(existingTeamMember.role, adminRole)),
    [existingTeamMember, teamMember, adminRole]
  );

  const updateTeamMemberSettings = useCallback(
    (newTeamMemberSettings: Partial<TeamMember>) => {
      setTeamMember((prevTeamMemberSettings) => {
        return { ...prevTeamMemberSettings, ...newTeamMemberSettings };
      });
    },
    []
  );

  const showError = useCallback(async () => {
    if (teamMember.email.trim() === "" || !teamMember.email.includes("@")) {
      setMemberEmailError("You must enter a valid email.");
      return true;
    }
    // Check if email is already in use
    const userData = await fetchUserDataFromEmail(teamMember.email);
    if (userData && userData.uid !== existingTeamMember?.uid) {
      setMemberEmailError("This email is already in use for another account.");
      return true;
    }
    if (teamMember.fullName.trim() === "") {
      setMemberNameError("You must enter a full name.");
      return true;
    }
    // Check if email is already in use
    if (!adminRole) {
      setAlertText("You must select a role for this team member.");
      return true;
    }
    return false;
  }, [teamMember.email, teamMember.fullName, adminRole, existingTeamMember]);

  const confirmOnPress = useCallback(async () => {
    const isError = await showError();
    if (isError) {
      return;
    }

    const finalTeamMember: TeamMember = {
      ...teamMember,
      role: adminRole || TeamMemberRole.FULL_ACCESS,
      email: teamMember.email.trim().toLowerCase(),
    };

    setSavingMember(true);
    const response = await (existingTeamMember
      ? API.profile.updateAccountTeamMember
      : API.profile.createAccountTeamMember)({
      uid: accountData.uid,
      teamMember: finalTeamMember,
    });

    if (!response.success) {
      setAlertText(
        "There was an error creating this team member. Verify that the email is valid and not currently in use."
      );
      setSavingMember(false);
      return;
    }

    showNotificationBanner(
      dispatch,
      existingTeamMember ? "Updated Team Member" : "Created Team Member",
      NotificationType.AFFIRMATIVE
    );
    setIsVisible(false);
    setSavingMember(false);
  }, [
    showError,
    teamMember,
    adminRole,
    existingTeamMember,
    dispatch,
    setIsVisible,
    accountData.uid,
  ]);

  return isVisible ? (
    <PopupModalContainer
      headerComp={
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <Icon icon="mdi:account-group" height={35} color={Colors.BLACK} />
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="sectionTitle">
              {existingTeamMember ? "Edit" : "New"} Team Member
            </span>
            <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
              Enter a name and valid email to create a new team member account.
              Then share the login details so they can join your Markit team.
            </span>
          </div>
        </div>
      }
      valueComp={
        <div className="ColumnNormal" style={{ gap: 20, paddingBottom: 20 }}>
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <span className="bodyMedium">Enter Valid Email</span>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <CustomTextField
                value={teamMember.email}
                placeholder="Enter Valid Email..."
                inputMode="email"
                borderRadius={12}
                backgroundColor={Colors.WHITE1}
                onChange={(change: any) => {
                  setMemberEmailError("");
                  updateTeamMemberSettings({
                    email: change.target.value,
                  });
                }}
                disabled={
                  memberUserData &&
                  !memberUserData.haveNotFinishedInitialStartup
                }
                error={memberEmailError !== ""}
                altMarginBottom={0}
              />
              {memberEmailError ? (
                <span style={styles.errorText}>{memberEmailError}</span>
              ) : null}
            </div>
          </div>
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <span className="bodyMedium">Full Name</span>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <CustomTextField
                value={teamMember.fullName}
                placeholder="Enter Full Name..."
                inputMode="text"
                borderRadius={12}
                backgroundColor={Colors.WHITE1}
                onChange={(change: any) => {
                  setMemberNameError("");
                  updateTeamMemberSettings({
                    fullName: change.target.value,
                  });
                }}
                disabled={
                  memberUserData &&
                  !memberUserData.haveNotFinishedInitialStartup
                }
                error={memberNameError !== ""}
                altMarginBottom={0}
              />
              {memberNameError ? (
                <span style={styles.errorText}>{memberNameError}</span>
              ) : null}
            </div>
          </div>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon icon="ion:cog" height={17} color={Colors.BLACK} />
            <span className="bodyMedium">Roles & Permissions</span>
          </div>
          <div className="AlignedRowSpaced">
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="bodyMedium">Full Admin</span>
              <span className="smallBodySubtext">
                Full access to all features and texting abilities
              </span>
            </div>
            <CustomCheckbox
              checked={adminRole === TeamMemberRole.FULL_ACCESS}
              onChange={() => {
                setAdminRole(
                  adminRole === TeamMemberRole.FULL_ACCESS
                    ? undefined
                    : TeamMemberRole.FULL_ACCESS
                );
              }}
              style={{ paddingRight: 0 }}
            />
          </div>
          <div className="AlignedRowSpaced">
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="bodyMedium">Support</span>
              <span className="smallBodySubtext">
                Can only respond to two way conversations
              </span>
            </div>
            <CustomCheckbox
              checked={
                adminRole === TeamMemberRole.TWO_WAY_SUPPORT ||
                adminRole === TeamMemberRole.FULL_ACCESS
              }
              onChange={() =>
                setAdminRole(
                  adminRole === TeamMemberRole.TWO_WAY_SUPPORT
                    ? undefined
                    : TeamMemberRole.TWO_WAY_SUPPORT
                )
              }
              style={{
                paddingRight: 0,
                pointerEvents:
                  adminRole === TeamMemberRole.FULL_ACCESS ? "none" : "all",
                opacity: adminRole === TeamMemberRole.FULL_ACCESS ? 0.4 : 1,
              }}
            />
          </div>
          <AlertContainer
            headerComp={alertText}
            closeModal={() => setAlertText("")}
            hideModal={alertText === ""}
          />
        </div>
      }
      fixedBottomContent={
        <div className="ColumnNormal" style={{ gap: 10 }}>
          <HorizontalDivider />
          <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={() => setIsVisible(false)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              altPaddingHorz={12}
              altPaddingVert={14}
              altBorderRadius={12}
              containerStyles={{
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={existingTeamMember ? "Save" : "Create"}
              onPress={confirmOnPress}
              loading={savingMember}
              disabled={existingTeamMember && !madeChanges}
              altColor={Colors.BLACK}
              altPaddingHorz={12}
              altPaddingVert={14}
              altBorderRadius={12}
            />
          </div>
        </div>
      }
      noExit
      closeModal={() => setIsVisible(false)}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  ) : null;
};

export default CreateTeamMemberModal;
